import { useState } from 'react'
import type { Campus, CampusType } from '@fellowship/shared'
import { useCampuses, useCreateCampus, useUpdateCampus, useArchiveCampus } from '../../hooks/useChurches'
import { AdminCard } from './AdminAtoms'
import { AdmBtn } from './AdminAtoms'
import { AdmIcn } from './AdminIcons'

const CAMPUS_TYPE_LABELS: Record<CampusType, string> = {
  main: 'Main',
  satellite: 'Satellite',
  school: 'School',
  community_center: 'Community Center',
}

const EMPTY_FORM = {
  name: '',
  type: 'satellite' as CampusType,
  addressLine1: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
}

type FormState = typeof EMPTY_FORM

interface CampusFormProps {
  initial?: Partial<FormState>
  onSave: (data: FormState) => void
  onCancel: () => void
  saving: boolean
  error?: string
}

function CampusForm({ initial, onSave, onCancel, saving, error }: CampusFormProps) {
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM, ...initial })
  const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }))

  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl bg-s2 border border-line-soft">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="eyebrow text-[9.5px]">Campus name</label>
          <input
            className="input text-[13px]"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="North Campus"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="eyebrow text-[9.5px]">Type</label>
          <select
            className="input text-[13px]"
            value={form.type}
            onChange={(e) => set('type', e.target.value as CampusType)}
          >
            {(Object.keys(CAMPUS_TYPE_LABELS) as CampusType[]).filter((t) => t !== 'main').map((t) => (
              <option key={t} value={t}>{CAMPUS_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label className="eyebrow text-[9.5px]">Street address</label>
        <input
          className="input text-[13px]"
          value={form.addressLine1}
          onChange={(e) => set('addressLine1', e.target.value)}
          placeholder="123 Main St"
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="flex flex-col gap-1 col-span-1">
          <label className="eyebrow text-[9.5px]">City</label>
          <input className="input text-[13px]" value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Springfield" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="eyebrow text-[9.5px]">State</label>
          <input className="input text-[13px]" value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="CA" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="eyebrow text-[9.5px]">ZIP</label>
          <input className="input text-[13px]" value={form.zip} onChange={(e) => set('zip', e.target.value)} placeholder="90210" />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label className="eyebrow text-[9.5px]">Phone (optional)</label>
        <input className="input text-[13px]" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(555) 000-0000" />
      </div>

      {error && <p className="text-[12px] text-warn m-0">{error}</p>}

      <div className="flex gap-2 justify-end">
        <AdmBtn size="sm" variant="ghost" onClick={onCancel}>Cancel</AdmBtn>
        <AdmBtn
          size="sm"
          variant="primary"
          onClick={() => onSave(form)}
          disabled={saving || !form.name.trim()}
        >
          {saving ? 'Saving…' : 'Save campus'}
        </AdmBtn>
      </div>
    </div>
  )
}

function CampusRow({
  campus,
  churchId,
  onEdit,
}: {
  campus: Campus
  churchId: string
  onEdit: (campus: Campus) => void
}) {
  const archive = useArchiveCampus(churchId)
  const [confirming, setConfirming] = useState(false)
  const isMain = campus.type === 'main'

  const address = [campus.addressLine1, campus.city, campus.state].filter(Boolean).join(', ')

  return (
    <div className="flex items-center gap-3 py-3 border-t border-line-soft first:border-t-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-medium">{campus.name}</span>
          <span
            className="text-[10px] font-mono tracking-[0.06em] px-[6px] py-[1px] rounded bg-s2 border border-line-soft text-muted"
          >
            {CAMPUS_TYPE_LABELS[campus.type]}
          </span>
        </div>
        {address && <div className="text-[11.5px] text-muted mt-[2px]">{address}</div>}
      </div>

      {!isMain && (
        confirming ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[12px] text-warn">Archive?</span>
            <AdmBtn
              size="sm"
              variant="danger"
              onClick={() => { archive.mutate(campus.id); setConfirming(false) }}
            >
              Yes
            </AdmBtn>
            <AdmBtn size="sm" variant="ghost" onClick={() => setConfirming(false)}>No</AdmBtn>
          </div>
        ) : (
          <div className="flex gap-1.5 shrink-0">
            <AdmBtn size="sm" variant="ghost" onClick={() => onEdit(campus)}>
              <AdmIcn name="edit" size={12} /> Edit
            </AdmBtn>
            <AdmBtn size="sm" variant="ghost" onClick={() => setConfirming(true)}>
              <AdmIcn name="x" size={12} />
            </AdmBtn>
          </div>
        )
      )}

      {isMain && (
        <AdmBtn size="sm" variant="ghost" onClick={() => onEdit(campus)}>
          <AdmIcn name="edit" size={12} /> Edit
        </AdmBtn>
      )}
    </div>
  )
}

export function CampusManagerCard({ churchId, canAddCampus = true, upgradeUrl }: { churchId: string; canAddCampus?: boolean; upgradeUrl?: string }) {
  const { data, isLoading } = useCampuses(churchId)
  const campuses = data?.data ?? []
  const create = useCreateCampus(churchId)
  const update = useUpdateCampus(churchId)

  const [mode, setMode] = useState<'idle' | 'adding' | 'editing'>('idle')
  const [editingCampus, setEditingCampus] = useState<Campus | null>(null)
  const [formError, setFormError] = useState<string | undefined>()

  function startEdit(campus: Campus) {
    setEditingCampus(campus)
    setMode('editing')
    setFormError(undefined)
  }

  function cancel() {
    setMode('idle')
    setEditingCampus(null)
    setFormError(undefined)
  }

  async function handleCreate(form: FormState) {
    setFormError(undefined)
    try {
      await create.mutateAsync({
        name: form.name,
        type: form.type,
        country: 'US',
        addressLine1: form.addressLine1 || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        zip: form.zip || undefined,
        phone: form.phone || undefined,
      })
      cancel()
    } catch (e) {
      setFormError((e as Error).message ?? 'Something went wrong')
    }
  }

  async function handleUpdate(form: FormState) {
    if (!editingCampus) return
    setFormError(undefined)
    try {
      await update.mutateAsync({
        campusId: editingCampus.id,
        data: {
          name: form.name,
          type: form.type,
          addressLine1: form.addressLine1 || undefined,
          city: form.city || undefined,
          state: form.state || undefined,
          zip: form.zip || undefined,
          phone: form.phone || undefined,
        },
      })
      cancel()
    } catch (e) {
      setFormError((e as Error).message ?? 'Something went wrong')
    }
  }

  return (
    <AdminCard>
      <div className="flex items-center justify-between mb-3">
        <div className="serif text-[17px]">Campuses</div>
        {mode === 'idle' && (
          canAddCampus ? (
            <AdmBtn size="sm" variant="ghost" icon="plus" onClick={() => { setMode('adding'); setFormError(undefined) }}>
              Add campus
            </AdmBtn>
          ) : (
            <a
              href={upgradeUrl}
              className="inline-flex items-center gap-1 text-[12px] text-muted border border-line-soft rounded-full px-2.5 py-1 cursor-pointer hover:border-primary hover:text-primary transition-colors no-underline"
              title="Connection plan required for multiple campuses"
            >
              <AdmIcn name="lock" size={11} /> Upgrade to add
            </a>
          )
        )}
      </div>

      {isLoading && <div className="text-[12px] text-muted py-2">Loading…</div>}

      {!isLoading && campuses.map((campus) => (
        mode === 'editing' && editingCampus?.id === campus.id ? (
          <div key={campus.id} className="py-2 border-t border-line-soft first:border-t-0">
            <CampusForm
              initial={{
                name: campus.name,
                type: campus.type === 'main' ? 'main' : campus.type,
                addressLine1: campus.addressLine1 ?? '',
                city: campus.city ?? '',
                state: campus.state ?? '',
                zip: campus.zip ?? '',
                phone: campus.phone ?? '',
              }}
              onSave={handleUpdate}
              onCancel={cancel}
              saving={update.isPending}
              error={formError}
            />
          </div>
        ) : (
          <CampusRow key={campus.id} campus={campus} churchId={churchId} onEdit={startEdit} />
        )
      ))}

      {mode === 'adding' && (
        <div className="mt-3">
          <CampusForm
            onSave={handleCreate}
            onCancel={cancel}
            saving={create.isPending}
            error={formError}
          />
        </div>
      )}
    </AdminCard>
  )
}
